import { RotateCcw } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Select } from '../../design/Select';
import { ToggleRow } from './primitives';

/** Ein Eintrag aus backend/server/games/ark-gameplay.js (kommt über den Steam-Katalog). */
export interface ArkField {
  key: string;
  group: string;
  label: string;
  hint?: string;
  target: 'server' | 'game';
  type: 'float' | 'int' | 'bool' | 'select';
  def: number | boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: number; label: string }[];
}

type Values = Record<string, any>;

// Voreinstellungen ändern nur die genannten Werte – der Rest bleibt, wie er ist.
const PRESETS: { label: string; hint: string; values: Values | 'official' }[] = [
  { label: 'Offiziell', hint: 'Alle Multiplikatoren auf ×1 wie auf offiziellen Servern', values: 'official' },
  {
    label: 'Entspannt ×2', hint: 'XP, Zähmen, Ernte und Zucht doppelt so schnell',
    values: { XPMultiplier: 2, TamingSpeedMultiplier: 2, HarvestAmountMultiplier: 2, EggHatchSpeedMultiplier: 2, BabyMatureSpeedMultiplier: 2, MatingIntervalMultiplier: 0.5, BabyCuddleIntervalMultiplier: 0.5 },
  },
  {
    label: 'Schnell ×5', hint: 'XP, Zähmen, Ernte und Zucht fünfmal so schnell',
    values: { XPMultiplier: 5, TamingSpeedMultiplier: 5, HarvestAmountMultiplier: 5, EggHatchSpeedMultiplier: 5, BabyMatureSpeedMultiplier: 5, MatingIntervalMultiplier: 0.2, BabyCuddleIntervalMultiplier: 0.2 },
  },
  {
    label: 'Weniger Hunger & Ausdauer', hint: 'Ausdauer-, Hunger- und Durstverbrauch halbiert',
    values: { PlayerCharacterStaminaDrainMultiplier: 0.5, PlayerCharacterFoodDrainMultiplier: 0.5, PlayerCharacterWaterDrainMultiplier: 0.5 },
  },
];

const num = (n: unknown) => Number(n).toLocaleString('de-DE', { maximumFractionDigits: 3 });

/** ARK-Spielregeln: Voreinstellungen oben, darunter die Werte nach Themen. */
export const ArkGameplayFields = ({ fields, values, onChange }: { fields: ArkField[]; values: Values; onChange: (v: Values) => void }) => {
  const set = (key: string, v: unknown) => onChange({ ...values, [key]: v });
  const applyPreset = (p: (typeof PRESETS)[number]) => {
    const patch = p.values === 'official'
      ? Object.fromEntries(fields.filter((f) => f.type === 'float').map((f) => [f.key, f.def]))
      : p.values;
    onChange({ ...values, ...patch });
  };
  const groups = [...new Set(fields.map((f) => f.group))];
  const changed = fields.filter((f) => f.type === 'float' && Number(values[f.key]) !== Number(f.def)).length;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-fg-muted font-medium mb-2">Schnell einstellen</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p)}
              title={p.hint}
              className="px-3 py-1.5 rounded-lg border border-line bg-white/[0.03] text-xs font-medium text-fg hover:border-ember/50 hover:text-ember transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-fg-muted mt-2">
          {changed ? `${changed} Wert(e) weichen von „offiziell“ (×1) ab.` : 'Alles wie auf offiziellen Servern (×1).'} Zum Übernehmen unten speichern.
        </p>
      </div>

      {groups.map((g) => (
        <div key={g}>
          <div className="text-[11px] uppercase tracking-wider text-fg-muted font-medium mb-2">{g}</div>
          <div className="rounded-xl border border-line divide-y divide-line">
            {fields.filter((f) => f.group === g).map((f) => (
              <Row key={f.key} f={f} value={values[f.key]} onChange={(v) => set(f.key, v)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const Label = ({ f }: { f: ArkField }) => (
  <div className="min-w-0">
    <div className="text-sm text-fg">{f.label}</div>
    {f.hint && <div className="text-xs text-fg-muted mt-0.5">{f.hint}</div>}
  </div>
);

const Row = ({ f, value, onChange }: { f: ArkField; value: any; onChange: (v: unknown) => void }) => {
  if (f.type === 'bool') {
    return (
      <div className="px-4 py-3">
        <ToggleRow label={f.label} hint={f.hint} value={!!value} onChange={onChange} />
      </div>
    );
  }

  if (f.type === 'select') {
    const current = value ?? f.def;
    const opts = (f.options || []).map((o) => ({ value: String(o.value), label: o.label }));
    if (!opts.some((o) => Number(o.value) === Number(current))) opts.push({ value: String(current), label: `Eigener Wert (${num(current)})` });
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <Label f={f} />
        <div className="w-full sm:w-72">
          <Select value={String(current)} options={opts} onChange={(v) => onChange(Number(v))} />
        </div>
      </div>
    );
  }

  const isDefault = value !== '' && Number(value) === Number(f.def);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <Label f={f} />
      <div className="flex items-center gap-1.5 shrink-0">
        {!isDefault && (
          <button
            type="button"
            onClick={() => onChange(f.def)}
            title={`Zurück auf Standard (${num(f.def)})`}
            aria-label={`${f.label} zurück auf Standard`}
            className="p-1 rounded-md text-fg-muted hover:text-fg hover:bg-white/[0.06]"
          >
            <RotateCcw size={13} />
          </button>
        )}
        <div className={cn('flex items-center rounded-lg border bg-black/25 pr-2.5', isDefault ? 'border-line' : 'border-ember/50')}>
          <input
            type="number"
            inputMode="decimal"
            step={f.step ?? 0.1}
            min={f.min}
            max={f.max}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            aria-label={f.label}
            className="w-20 bg-transparent pl-2.5 pr-0.5 py-1.5 text-sm text-fg text-right tabular-nums focus:outline-none"
          />
          {/* direkt an der Zahl („2x"), damit es nicht wie ein Schließen-Kreuz aussieht */}
          <span className="text-sm text-fg-muted" aria-hidden="true">x</span>
        </div>
      </div>
    </div>
  );
};
